import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Search, Filter, MoreVertical, ShieldCheck, Mail, Phone, Check, X, Eye, Trash2, Users, CheckCircle, Hourglass, XCircle, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import { confirmAction } from '../../utils/confirmToast';
import api from '../../api/axios';
import styles from './Residents.module.css';

const Residents = () => {
  const { searchQuery: globalSearchQuery } = useOutletContext() || { searchQuery: '' };
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [residents, setResidents] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filter and Pagination State
  const [localSearch, setLocalSearch] = useState('');
  const [filterType, setFilterType] = useState('All Types');
  const [filterStatus, setFilterStatus] = useState('All Status');
  const [filterBlock, setFilterBlock] = useState('All Blocks');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchResidents();
  }, []);

  const fetchResidents = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/residents');
      setResidents(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  // Derived Stats
  const totalResidents = residents.length;
  const approvedCount = residents.filter(r => r.approvalStatus === 'Approved').length;
  const pendingCount = residents.filter(r => r.approvalStatus === 'Pending').length;
  const rejectedCount = residents.filter(r => r.approvalStatus === 'Rejected').length;
  
  const approvedPerc = totalResidents ? ((approvedCount/totalResidents)*100).toFixed(1) : 0;
  const pendingPerc = totalResidents ? ((pendingCount/totalResidents)*100).toFixed(1) : 0;
  const rejectedPerc = totalResidents ? ((rejectedCount/totalResidents)*100).toFixed(1) : 0;

  // Derived Filtered List
  const activeSearchQuery = localSearch || globalSearchQuery;

  const filteredResidents = residents.filter(res => {
    const matchesSearch = (res.fullName && res.fullName.toLowerCase().includes(activeSearchQuery.toLowerCase())) ||
      (res.phone && res.phone.includes(activeSearchQuery)) ||
      (res.email && res.email.toLowerCase().includes(activeSearchQuery.toLowerCase())) ||
      (res.flatNumber && res.flatNumber.toLowerCase().includes(activeSearchQuery.toLowerCase()));
      
    const matchesType = filterType === 'All Types' || res.residentType === filterType;
    const matchesStatus = filterStatus === 'All Status' || res.approvalStatus === filterStatus;
    const matchesBlock = filterBlock === 'All Blocks' || res.wing === filterBlock;
    
    return matchesSearch && matchesType && matchesStatus && matchesBlock;
  });

  const totalPages = Math.ceil(filteredResidents.length / itemsPerPage) || 1;
  const currentResidents = filteredResidents.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleResetFilters = () => {
    setLocalSearch('');
    setFilterType('All Types');
    setFilterStatus('All Status');
    setFilterBlock('All Blocks');
    setCurrentPage(1);
  };

  const handleStatusChange = async (id, status) => {
    try {
      const { data } = await api.put(`/admin/residents/${id}/status`, { status });
      setResidents(residents.map(r => r._id === id ? data : r));
      toast.success('Status updated successfully!');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = (id) => {
    confirmAction(
      'Are you sure you want to delete this resident? This action cannot be undone.',
      async () => {
        try {
          await api.delete(`/admin/residents/${id}`);
          setResidents(residents.filter(r => r._id !== id));
          toast.success('Resident deleted successfully');
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete resident');
        }
      }
    );
  };

  const [formData, setFormData] = useState({
    unit: '',
    name: '',
    type: 'Owner',
    phone: '',
    email: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newResident = {
      ...formData,
      id: Date.now(),
      status: 'Active'
    };
    setResidents([newResident, ...residents]);
    setFormData({ unit: '', name: '', type: 'Owner', phone: '', email: '' });
    setIsDrawerOpen(false);
    toast.success('Resident added successfully!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('/uploads')) {
      return `${import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '') : ''}${imagePath}`;
    }
    return imagePath;
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Resident Management" 
        subtitle="Manage all society residents, their details, approvals and unit information."
        breadcrumb={['Society Management', 'Residents']} 
        actionButton={
          <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
            <Plus size={16} /> Add Resident
          </button>
        }
      />

      {/* Stats Cards */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.iconTotal}`}>
            <Users size={24} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Total Residents</span>
            <span className={styles.statValue}>{totalResidents}</span>
            <span className={styles.statSub}>All registered residents</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.iconApproved}`}>
            <CheckCircle size={24} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Approved</span>
            <span className={styles.statValue}>{approvedCount}</span>
            <span className={styles.statSub}>{approvedPerc}% of total</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.iconPending}`}>
            <Hourglass size={24} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Pending</span>
            <span className={styles.statValue}>{pendingCount}</span>
            <span className={styles.statSub}>{pendingPerc}% of total</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.iconRejected}`}>
            <XCircle size={24} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Rejected</span>
            <span className={styles.statValue}>{rejectedCount}</span>
            <span className={styles.statSub}>{rejectedPerc}% of total</span>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        {/* Filter Bar */}
        <div className={styles.filterBar}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input 
              type="text" 
              placeholder="Search by name, unit, phone or email..." 
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
          </div>
          
          <select className={styles.filterDropdown} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="All Types">All Types</option>
            <option value="Owner">Owner</option>
            <option value="Tenant">Tenant</option>
            <option value="Co-Owner">Co-Owner</option>
          </select>
          
          <select className={styles.filterDropdown} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="All Status">All Status</option>
            <option value="Approved">Approved</option>
            <option value="Pending">Pending</option>
            <option value="Rejected">Rejected</option>
          </select>

          <select className={styles.filterDropdown} value={filterBlock} onChange={(e) => setFilterBlock(e.target.value)}>
            <option value="All Blocks">All Blocks</option>
            <option value="A">Tower A</option>
            <option value="B">Tower B</option>
            <option value="C">Block C</option>
            <option value="D">Block D</option>
          </select>

          <select className={styles.filterDropdown} value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>
          
          <button className={styles.resetBtn} onClick={handleResetFilters}>
            <RotateCcw size={16} /> Reset
          </button>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>UNIT</th>
                <th>RESIDENT DETAILS</th>
                <th>TYPE</th>
                <th>CONTACT INFO</th>
                <th>STATUS</th>
                <th className={styles.actionsHeader}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7" style={{textAlign: 'center', padding: '20px'}}>Loading...</td></tr> : 
                currentResidents.length === 0 ? <tr><td colSpan="7" style={{textAlign: 'center', padding: '20px'}}>No residents found.</td></tr> :
                currentResidents.map((res, index) => (
                <tr key={res._id}>
                  <td className={styles.tdId}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>
                    <div className={styles.unitInfo}>
                      <span className={styles.unitBlock}>{res.wing && res.wing.length > 1 ? res.wing : `Tower ${res.wing || '-'}`}</span>
                      <span className={styles.unitFlat}>{res.wing}-{res.flatNumber}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.residentInfo}>
                      <div className={styles.avatarSmall} style={!res.profileImage ? { backgroundColor: '#e2e8f0', color: '#10b981' } : { padding: 0, overflow: 'hidden' }}>
                        {res.profileImage ? (
                          <img src={getImageUrl(res.profileImage)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          res.fullName ? res.fullName.charAt(0).toUpperCase() : 'U'
                        )}
                      </div>
                      <div className={styles.residentNameGroup}>
                        <strong>{res.fullName || 'Unknown'}</strong>
                        <span>Family Members: {res.familyCount || 0}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.badgeType} ${res.residentType === 'Tenant' ? styles.typeTenant : styles.typeOwner}`}>
                      {res.residentType || 'Owner'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.contactInfo}>
                      <span><Phone size={14} /> {res.phone || '-'}</span>
                      <span><Mail size={14} /> {res.email}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.statusGroup}>
                      <span className={`${styles.badgeStatus} ${res.approvalStatus === 'Approved' ? styles.active : (res.approvalStatus === 'Rejected' ? styles.inactive : styles.pending)}`}>
                        {res.approvalStatus === 'Approved' && <ShieldCheck size={12} />}
                        {res.approvalStatus === 'Pending' && <Hourglass size={12} />}
                        {res.approvalStatus === 'Rejected' && <XCircle size={12} />}
                        {res.approvalStatus}
                      </span>
                      <span className={styles.statusDate}>on {formatDate(res.createdAt || res.updatedAt || new Date().toISOString())}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      {res.approvalStatus === 'Pending' && (
                        <>
                          <button className={`${styles.actionBtnSquare} ${styles.viewBtn}`} onClick={() => { setSelectedResident(res); setIsDrawerOpen(false); }} title="View Details">
                            <Eye size={16} />
                          </button>
                          <button className={`${styles.actionBtnSquare} ${styles.approveBtn}`} onClick={() => handleStatusChange(res._id, 'Approved')} title="Approve">
                            <Check size={16} strokeWidth={3} />
                          </button>
                          <button className={`${styles.actionBtnSquare} ${styles.rejectBtn}`} onClick={() => handleStatusChange(res._id, 'Rejected')} title="Reject">
                            <X size={16} strokeWidth={3} />
                          </button>
                          <button className={`${styles.actionBtnSquare} ${styles.deleteBtn}`} onClick={() => handleDelete(res._id)} title="Delete Resident">
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                      
                      {res.approvalStatus === 'Approved' && (
                        <>
                          <button className={`${styles.actionBtnSquare} ${styles.viewBtn}`} onClick={() => { setSelectedResident(res); setIsDrawerOpen(false); }} title="View Details">
                            <Eye size={16} />
                          </button>
                          <button className={`${styles.actionBtnSquare} ${styles.approveBtn}`} onClick={() => handleStatusChange(res._id, 'Approved')} title="Approve" disabled style={{opacity: 0.5}}>
                            <Check size={16} strokeWidth={3} />
                          </button>
                          <button className={`${styles.actionBtnSquare} ${styles.rejectBtn}`} onClick={() => handleStatusChange(res._id, 'Rejected')} title="Reject">
                            <X size={16} strokeWidth={3} />
                          </button>
                          <button className={`${styles.actionBtnSquare} ${styles.deleteBtn}`} onClick={() => handleDelete(res._id)} title="Delete Resident">
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}

                      {res.approvalStatus === 'Rejected' && (
                        <>
                          <button className={`${styles.actionBtnSquare} ${styles.viewBtn}`} onClick={() => { setSelectedResident(res); setIsDrawerOpen(false); }} title="View Details">
                            <Eye size={16} />
                          </button>
                          <button className={`${styles.actionBtnSquare} ${styles.approveBtn}`} onClick={() => handleStatusChange(res._id, 'Approved')} title="Re-approve Resident">
                            <Check size={16} strokeWidth={3} />
                          </button>
                          <button className={`${styles.actionBtnSquare} ${styles.rejectBtn}`} onClick={() => handleStatusChange(res._id, 'Rejected')} title="Reject" disabled style={{opacity: 0.5}}>
                            <X size={16} strokeWidth={3} />
                          </button>
                          <button className={`${styles.actionBtnSquare} ${styles.deleteBtn}`} onClick={() => handleDelete(res._id)} title="Delete Resident">
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards (Visible only on small screens) */}
        <div className={styles.mobileList}>
          {loading ? <div style={{textAlign: 'center', padding: '20px'}}>Loading...</div> : 
            currentResidents.length === 0 ? <div style={{textAlign: 'center', padding: '20px'}}>No residents found.</div> :
            currentResidents.map((res) => (
            <div key={res._id} className={styles.mobileCard}>
              <div className={styles.cardHeaderArea}>
                <div className={styles.residentInfo}>
                  <div className={styles.avatarSmall} style={!res.profileImage ? { backgroundColor: '#e2e8f0', color: '#10b981' } : { padding: 0, overflow: 'hidden' }}>
                    {res.profileImage ? (
                      <img src={getImageUrl(res.profileImage)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      res.fullName ? res.fullName.charAt(0).toUpperCase() : 'U'
                    )}
                  </div>
                  <div className={styles.residentNameGroup}>
                    <strong>{res.fullName || 'Unknown'}</strong>
                    <span>{res.wing}-{res.flatNumber} &bull; Family: {res.familyCount || 0}</span>
                  </div>
                </div>
                <span className={`${styles.badgeType} ${res.residentType === 'Tenant' ? styles.typeTenant : styles.typeOwner}`}>
                  {res.residentType || 'Owner'}
                </span>
              </div>
              
              <div className={styles.cardMidArea}>
                <div className={styles.contactInfo}>
                  <span><Phone size={14} /> {res.phone || '-'}</span>
                  <span><Mail size={14} /> {res.email}</span>
                </div>
              </div>
              
              <div className={styles.cardBottomArea}>
                <div className={styles.statusGroup}>
                  <span className={`${styles.badgeStatus} ${res.approvalStatus === 'Approved' ? styles.active : (res.approvalStatus === 'Rejected' ? styles.inactive : styles.pending)}`}>
                    {res.approvalStatus}
                  </span>
                  <span className={styles.statusDate}>on {formatDate(res.createdAt || res.updatedAt || new Date().toISOString())}</span>
                </div>
                
                <div className={styles.actions}>
                  {res.approvalStatus === 'Pending' && (
                    <>
                      <button className={`${styles.actionBtnSquare} ${styles.approveBtn}`} onClick={() => handleStatusChange(res._id, 'Approved')}><Check size={16} /></button>
                      <button className={`${styles.actionBtnSquare} ${styles.rejectBtn}`} onClick={() => handleStatusChange(res._id, 'Rejected')}><X size={16} /></button>
                    </>
                  )}
                  {res.approvalStatus === 'Approved' && (
                    <button className={`${styles.actionBtnSquare} ${styles.rejectBtn}`} onClick={() => handleStatusChange(res._id, 'Rejected')}><X size={16} /></button>
                  )}
                  {res.approvalStatus === 'Rejected' && (
                    <button className={`${styles.actionBtnSquare} ${styles.approveBtn}`} onClick={() => handleStatusChange(res._id, 'Approved')}><Check size={16} /></button>
                  )}
                  <button className={`${styles.actionBtnSquare} ${styles.viewBtn}`} onClick={() => { setSelectedResident(res); setIsDrawerOpen(false); }}><Eye size={16} /></button>
                  <button className={`${styles.actionBtnSquare} ${styles.deleteBtn}`} onClick={() => handleDelete(res._id)}><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Footer */}
        <div className={styles.paginationFooter}>
          <div className={styles.paginationInfo}>
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredResidents.length)} of {filteredResidents.length} residents
          </div>
          <div className={styles.paginationControls}>
            <button 
              className={styles.pageBtn} 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(c => Math.max(1, c - 1))}
            >
              <ChevronLeft size={16} />
            </button>
            {[...Array(totalPages)].map((_, idx) => (
              <button 
                key={idx} 
                className={`${styles.pageBtn} ${currentPage === idx + 1 ? styles.pageBtnActive : ''}`}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </button>
            ))}
            <button 
              className={styles.pageBtn} 
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        title="Add New Resident"
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Unit / Flat Number</label>
            <input name="unit" value={formData.unit} onChange={handleChange} required placeholder="e.g. A-101" />
          </div>
          <div className="formGroup">
            <label>Full Name</label>
            <input name="name" value={formData.name} onChange={handleChange} required placeholder="Enter full name" />
          </div>
          <div className="formGroup">
            <label>Resident Type</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="Owner">Owner</option>
              <option value="Tenant">Tenant</option>
              <option value="Co-Owner">Co-Owner</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Phone Number</label>
            <input name="phone" value={formData.phone} onChange={handleChange} required placeholder="Contact number" />
          </div>
          <div className="formGroup">
            <label>Email Address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Email address" />
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Save Resident</button>
          </div>
        </form>
      </Drawer>

      <Drawer 
        isOpen={!!selectedResident} 
        onClose={() => setSelectedResident(null)} 
        title="Resident Details"
      >
        {selectedResident && (
          <div className={styles.drawerContent}>
            <div className={styles.drawerHeader} style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '24px' }}>
              <div className={styles.avatarLarge} style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 'bold', color: '#64748b', overflow: 'hidden' }}>
                {selectedResident.profileImage ? (
                  <img src={getImageUrl(selectedResident.profileImage)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  selectedResident.fullName ? selectedResident.fullName.charAt(0).toUpperCase() : 'U'
                )}
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', color: '#0f172a' }}>{selectedResident.fullName}</h3>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '14px' }}>{selectedResident.residentType || 'Owner'} &bull; Block {selectedResident.wing || '-'} Flat {selectedResident.flatNumber || '-'}</p>
              </div>
            </div>

            <div className={styles.drawerSection} style={{ marginBottom: '24px' }}>
              <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0' }}>Contact Information</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748b' }}>Phone</span>
                <span style={{ fontWeight: 500, color: '#0f172a' }}>{selectedResident.phone || 'Not provided'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Email</span>
                <span style={{ fontWeight: 500, color: '#0f172a' }}>{selectedResident.email}</span>
              </div>
            </div>
            
            <div className={styles.drawerSection}>
              <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0' }}>Family Members ({selectedResident.familyCount || 0})</div>
              {selectedResident.familyMembers && selectedResident.familyMembers.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                  {selectedResident.familyMembers.map((member, i) => (
                    <div key={i} style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '4px' }}>{member.name}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        {member.relation} &bull; {member.age} yrs
                        {member.phone && <span> &bull; 📞 {member.phone}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#64748b', fontStyle: 'italic', fontSize: '14px' }}>No family members registered</div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Residents;
